import { CalculosInventario, Inventario } from 'src/drheranca/types';
import { ASSET_CATEGORIES } from '../definitions/assetCategories';
import { TIPOS_DIVIDA_QUE_ABATEM } from '../definitions/dividas';
import { ExcelCellValue, ExcelSheetData } from '../types';
import {
  getAssetDisplayValue,
  getInventarioAssets,
} from '../utils/assetValues';

export interface ComposicaoAssetRow {
  categoryKey: string;
  categoryLabel: string;
  rowDescription: string;
  descricao: string;
  value: number;
}

export interface ComposicaoMatrixColumn {
  categoryKey: string;
  name: string;
  value: number;
}

/** Linhas de bens com valor > 0 (lista UI e base da matriz Excel). */
export function collectComposicaoAssetRows(inventario: Inventario): ComposicaoAssetRow[] {
  const rows: ComposicaoAssetRow[] = [];
  ASSET_CATEGORIES.forEach(({ key, label, rowDescription }) => {
    const assets = getInventarioAssets(inventario, key);
    assets.forEach((a, idx) => {
      const val = getAssetDisplayValue(a);
      if (val > 0.01) {
        rows.push({
          categoryKey: key as string,
          categoryLabel: label,
          rowDescription,
          descricao: (a.descricao as string) || `${label} ${idx + 1}`,
          value: val,
        });
      }
    });
  });
  return rows;
}

export function collectComposicaoMatrixColumns(inventario: Inventario): ComposicaoMatrixColumn[] {
  return collectComposicaoAssetRows(inventario).map((r) => ({
    categoryKey: r.categoryKey,
    name: r.descricao,
    value: r.value,
  }));
}

export function getDividasQueAbatem(inventario: Inventario) {
  return (inventario.dividas || []).filter((d) =>
    TIPOS_DIVIDA_QUE_ABATEM.has(d?.tipo_divida ?? 'outros'),
  );
}

/** Planilha Composição — matriz (export Excel legado). */
export function buildComposicaoExcelSheet(
  inventario: Inventario,
  calculos: CalculosInventario,
): ExcelSheetData {
  const columns = collectComposicaoMatrixColumns(inventario);
  const header: ExcelCellValue[] = [
    'Categoria / Ativo',
    'Descrição',
    ...columns.map((c) => c.name),
    'Total',
  ];
  const rows: ExcelCellValue[][] = [header];

  ASSET_CATEGORIES.forEach(({ key, label, rowDescription }) => {
    const categoryAssets = columns.filter((c) => c.categoryKey === key);
    if (categoryAssets.length === 0) return;
    const catTotal = categoryAssets.reduce((s, c) => s + c.value, 0);
    const row: ExcelCellValue[] = [label, rowDescription];
    columns.forEach((col) => {
      row.push(col.categoryKey === key ? col.value : null);
    });
    row.push(catTotal);
    rows.push(row);
  });

  const patrimonioBrutoTotal = calculos.totalPatrimonioBruto ?? 0;
  rows.push([
    'Patrimônio Bruto Total',
    'Soma de todos os ativos do espólio (data do óbito)',
    ...columns.map(() => null),
    patrimonioBrutoTotal,
  ]);

  getDividasQueAbatem(inventario).forEach((d, idx) => {
    rows.push([
      `− ${d.descricao || `Dívida ${idx + 1}`}`,
      'Saldo devedor da dívida (reduz o patrimônio)',
      ...columns.map(() => null),
      -(d.saldo_devedor ?? 0),
    ]);
  });

  (calculos.financiamentosDetalhe || []).forEach((f) => {
    rows.push([
      `− Financ. ${f.descricao}`,
      'Saldo devedor do financiamento vinculado ao bem',
      ...columns.map(() => null),
      -(f.saldo_devedor ?? 0),
    ]);
  });

  const totalDividas = calculos.totalDividas ?? 0;
  if (totalDividas > 0) {
    const patrimonioLiquido =
      calculos.totalPatrimonioLiquido ?? patrimonioBrutoTotal - totalDividas;
    rows.push([
      'Patrimônio Líquido',
      'Patrimônio bruto menos dívidas e financiamentos',
      ...columns.map(() => null),
      patrimonioLiquido,
    ]);
  }

  const doacoesExcluidas = calculos.valorDoacoesExcluidas ?? 0;
  if (doacoesExcluidas > 0) {
    rows.push([
      '− Doações excluídas',
      'Liberalidades fora da legítima (não compõem o monte partível)',
      ...columns.map(() => null),
      -doacoesExcluidas,
    ]);
  }

  return {
    rows,
    colWidths: [32, 50, ...columns.map(() => 18), 18],
    valueColumnStart: 2,
  };
}
